import { Box, Paper, Container, SxProps, Theme, Typography, Link } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import React, { Suspense, useState } from "react";
import { useEffect } from "react";
import { BackendConfig, GetMemberResponse } from "../../typings/backend/index";
import { getAppData } from "../../services/backend-api";
import BuyMeACoffee from "./BuyMeACoffee";
import BecomePatron from "./BecomePatron";
import { Forum, MovieFilterOutlined, Notes } from "@mui/icons-material";
import { BoxProps } from "@mui/system";
import { DiscordIcon } from "@components/Svg";
const ChangelogModal = React.lazy(() => import("../Changelog/ChangelogModal"));

type Config = BackendConfig & GetMemberResponse["data_config"];
let configCache: Config | null = null;

function useConfig() {
  const [config, setConfig] = useState<Config | null>(null);

  useEffect(() => {
    if (configCache) {
      setConfig(configCache);
      return;
    }
    const { config: config_appData, data_config } = getAppData();
    if (config_appData && data_config) {
      const data = { ...config_appData, ...data_config };
      setConfig(data);
      configCache = data;
    }
  }, []);

  return config;
}

const sxLinkDiv: SxProps<Theme> = (theme) => ({
  padding: theme.spacing(0.5, 1),
  display: "flex",
});

const sxLink: SxProps<Theme> = {
  color: "text.primary",
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  // whiteSpace: "nowrap",
};

const useStyles = makeStyles((theme) => ({
  supportBtn: { margin: theme.spacing(0.5, 0.5, 1) },
}));

export const Footer: React.FC<{ style?: React.CSSProperties }> = ({ style }) => {
  const classes = useStyles();
  const config = useConfig();

  if (!config) return null;

  const PATREON_URL = config.patreon?.url;
  const BMC_URL = config.buymeacoffee?.url;

  const links = [
    { Component: ReleaseNotes, url: "relnotes" /* "fake" unique url needed for .map key */ },
    // { Icon: GoogleGroupsSvg, url: config.group_url, name: config.group_text },
    { Icon: Forum, url: config.forum_url, name: config.forum_text },
    { Icon: MovieFilterOutlined, url: config.sanne_site, name: config.sanne_text },
    { Icon: DiscordIcon, url: config.discord?.url, name: config.discord?.text },
  ].filter((link) => Boolean(link.url || link.Component));

  return (
    <Paper sx={{ zIndex: -1, paddingTop: 2, marginTop: 3 }} style={style}>
      <Container
        sx={(theme) => ({
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: theme.spacing(1, 0),
          "&:last-child": { paddingTop: 0 },
          // [theme.breakpoints.down("sm")]: { flexDirection: "row" },
          "@media (max-width: 500px)": { flexDirection: "column-reverse" },
        })}
        maxWidth="lg"
      >
        <LogoDiv />

        {/* Wraps text links & donation buttons */}
        <Box pl={[2, 2, 5]} sx={{ "@media (max-width: 380px)": { padding: 0 } }}>
          {/* Text links (release notes, google group, etc.) */}
          <Box
            display={"flex"}
            alignItems={"center"}
            justifyContent={"center"}
            mb={1}
            flexDirection={["column", "inherit", "inherit"]}
          >
            {/* Text links with icon */}
            {links.map((link) => {
              return link.Component ? (
                <link.Component key={link.url} />
              ) : (
                <Box sx={sxLinkDiv} key={link.url}>
                  <Link sx={sxLink} href={link.url} underline="hover" target="_blank" rel="noreferrer">
                    <link.Icon />
                    <Typography variant="subtitle2" pl={0.5} aria-label={link.name}>
                      {link.name}
                    </Typography>
                  </Link>
                </Box>
              );
            })}
          </Box>

          {/* Patreon & BMC link. */}
          <Box
            sx={(theme) => ({
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              [theme.breakpoints.down("sm")]: { flexDirection: "column" },
            })}
          >
            {PATREON_URL && <BecomePatron className={classes.supportBtn} _url={PATREON_URL} />}
            {BMC_URL && <BuyMeACoffee className={classes.supportBtn} _url={BMC_URL} />}
          </Box>
        </Box>
      </Container>
    </Paper>
  );
};

export default Footer;

const LogoDiv = (boxProps: BoxProps) => {
  return (
    <Box {...boxProps}>
      <Box
        component="img"
        src="/logo.png"
        alt={"Logo"}
        sx={(theme) => ({
          // width: 140,
          height: 142,
          [theme.breakpoints.down("sm")]: {
            // width: 160,
            height: 162,
          },
        })}
      />
    </Box>
  );
};

const ReleaseNotes = () => {
  const [open, setOpen] = useState(false);

  return (
    <Box sx={sxLinkDiv}>
      <Notes />
      <Typography
        sx={{ ...sxLink }}
        pl={0.5}
        component={Link}
        underline="hover"
        onClick={() => setOpen(true)}
        role="button"
        aria-label="Update Notes"
        variant="subtitle2"
        // textTransform={"capitalize"}
      >
        Update Notes
      </Typography>

      {open && (
        <Suspense fallback="">
          <ChangelogModal open={open} setOpen={setOpen} shouldFetch={open} />
        </Suspense>
      )}
    </Box>
  );
};

const GoogleGroupsSvg = () => {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 0 24 24" width="24px" fill="#FFFFFF">
      <path d="M0 0h24v24H0V0z" fill="none" />
      <path d="M9 13.75c-2.34 0-7 1.17-7 3.5V19h14v-1.75c0-2.33-4.66-3.5-7-3.5zM4.34 17c.84-.58 2.87-1.25 4.66-1.25s3.82.67 4.66 1.25H4.34zM9 12c1.93 0 3.5-1.57 3.5-3.5S10.93 5 9 5 5.5 6.57 5.5 8.5 7.07 12 9 12zm0-5c.83 0 1.5.67 1.5 1.5S9.83 10 9 10s-1.5-.67-1.5-1.5S8.17 7 9 7zm7.04 6.81c1.16.84 1.96 1.96 1.96 3.44V19h4v-1.75c0-2.02-3.5-3.17-5.96-3.44zM15 12c1.93 0 3.5-1.57 3.5-3.5S16.93 5 15 5c-.54 0-1.04.13-1.5.35.63.89 1 1.98 1 3.15s-.37 2.26-1 3.15c.46.22.96.35 1.5.35z" />
    </svg>
  );
};
